import { useGetChatEligibilityQuery } from "@api/chatManagement";
import { ShareChannelModal } from "@components/Chat/components/ShareChannelModal";
import { useShareToChat } from "@components/Chat/hooks/useShareToChat";
import {
	canShareToChat,
	getChatShareFromHistoryReport,
	isSportsbookHistoryReport,
	useChatAvailability,
	useIsLoggedIn,
} from "@components/Chat/utils";
import { BettoShareChannelModal } from "@layouts/betto/components/ShareChannelModal";
import {
	Amount,
	BonusInfo,
	ButtonText,
	ContentBody,
	ContentBodyActions,
	ContentDescription,
	ContentFooter,
	ContentHeader,
	ContentWrapper,
	CopyId,
	Date,
	Description,
	DetailsButton,
	FooterIdInfoWrapper,
	FooterInfoWrapper,
	HeaderTitle,
	Id,
	Note,
	PaybackInfo,
	ShareToChatButton,
	Status,
} from "./elements";

const ShareIcon = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true">
		<path
			d="M12 16V4m0 0L8 8m4-4l4 4M5 20h14"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const GameContentRow = ({
	report,
	index,
	translate,
	openDetailsIndex,
	setOpenDetailsIndex,
	canShareToChat,
	onShare,
	isSharing,
}) => {
	const areDetailsOpen = openDetailsIndex === index;
	const sharePayload = getChatShareFromHistoryReport(report);
	const showShareButton = canShareToChat && Boolean(sharePayload);

	const handleShareClick = () => {
		if (!sharePayload || isSharing) {
			return;
		}

		onShare(sharePayload);
	};

	return (
		<ContentWrapper key={index}>
			<ContentHeader>
				<Date>
					{moment(Number(report?.submissionTime_id?.split("#")[0])).format(
						"YYYY-MM-DD HH:mm"
					)}
				</Date>
				<HeaderTitle>
					{isSportsbookHistoryReport(report)
						? `SPORTSBOOK ${translate("GAME")}`
						: report?.roundId
							? `CASINO ${translate("GAME")}`
							: ""}
				</HeaderTitle>
			</ContentHeader>
			<ContentBody>
				<Amount>
					{translate("BET AMOUNT")}:{" "}
					{report?.walletContributions &&
					report?.walletContributions.length > 0 &&
					report?.walletContributions[0].amount.currency === "FreeSpin Token" ? (
						<>
							{report?.walletContributions[0]?.amount?.value ?? "-"}{" "}
							{report?.walletContributions[0]?.amount?.currency}{" "}
						</>
					) : (
						<>
							{getCurrencySymbol(report?.walletContributions[0]?.amount?.currency)}{" "}
							{report?.walletContributions[0]?.amount?.value ?? "-"}
						</>
					)}
				</Amount>
				<ContentBodyActions>
					{showShareButton && (
						<ShareToChatButton
							type="button"
							disabled={isSharing}
							onClick={handleShareClick}
							data-testid="share-to-chat-button">
							<ShareIcon />
							{translate("chat.shareToChat")}
						</ShareToChatButton>
					)}
					<Status status={report?.type || report?.transactionType?.split("#")[1]}>
						{report?.type
							? report?.type
							: report?.transactionType
								? report?.transactionType?.split("#")[1]
								: ""}
					</Status>
				</ContentBodyActions>
			</ContentBody>
			<ContentDescription>
				<Description>
					{translate("BALANCE AFTER TRANSACTION")}:{" "}
					{report?.walletContributions &&
						report?.walletContributions.length > 0 &&
						getCurrencySymbol(report?.walletContributions[0].amount.currency)}{" "}
					{report?.balanceAfterTransaction ?? "-"}{" "}
				</Description>
			</ContentDescription>
			{areDetailsOpen ? (
				window.PROFILE_HISTORY_HAS_IDS ? (
					<ContentFooter>
						<FooterIdInfoWrapper>
							<CopyId hasIds={window.PROFILE_HISTORY_HAS_IDS}>
								<CopyIcon
									onClick={() =>
										copyToClipboard(
											(report?.roundId || report?.couponId) &&
												report?.couponId
												? report?.couponId.split("#")[1]
												: report?.roundId
													? report?.roundId
															?.split("#")
															?.filter((part) => /\d/.test(part))
															?.join(" ")
													: "",
											translate
										)
									}
								/>
								{report?.couponId
									? translate("COUPON_ID")
									: report?.roundId
										? translate("ROUND_ID")
										: ""}
								<Id>
									{parseId(
										(report?.roundId || report?.couponId) && report?.couponId
											? report?.couponId.split("#")[1]
											: report?.roundId
												? report?.roundId
														?.split("#")
														?.filter((part) => /\d/.test(part))
														?.join(" ")
												: "",
										5
									)}
								</Id>
							</CopyId>
							<CopyId hasIds={window.PROFILE_HISTORY_HAS_IDS}>
								<CopyIcon
									onClick={() =>
										copyToClipboard(
											report?.transactionId && report?.transactionId,
											translate
										)
									}
								/>
								{translate("TRANSACTION_ID")}: {""}
								<Id>
									{parseId(report?.transactionId && report?.transactionId, 5)}
								</Id>
							</CopyId>
						</FooterIdInfoWrapper>
						<FooterInfoWrapper hasIds={window.PROFILE_HISTORY_HAS_IDS}>
							<BonusInfo>
								{translate("BONUS")}:{" "}
								{report?.walletContributions?.length > 0 &&
								report?.walletContributions[0]?.walletType == "Real"
									? "NO"
									: "YES"}
							</BonusInfo>
							<PaybackInfo>
								{translate("PAYBACK")}:{" "}
								{report?.transactionType &&
								report?.transactionType?.split("#")[1] == "Rollback"
									? translate("YES")
									: translate("NO")}
							</PaybackInfo>
						</FooterInfoWrapper>
						<Note>
							{translate("NOTE")}:{" "}
							{report?.note ? report?.note : translate("THERE IS NO NOTE")}
						</Note>
					</ContentFooter>
				) : (
					<ContentFooter>
						<FooterInfoWrapper>
							<CopyId>
								<CopyIcon
									onClick={() =>
										copyToClipboard(
											(report?.roundId || report?.couponId) &&
												report?.couponId
												? report?.couponId.split("#")[1]
												: report?.roundId
													? report?.roundId
															?.split("#")
															?.filter((part) => /\d/.test(part))
															?.join(" ")
													: "",
											translate
										)
									}
								/>
								{translate("COL-ID")}: {""}
								<Id>
									{parseId(
										(report?.roundId || report?.couponId) && report?.couponId
											? report?.couponId.split("#")[1]
											: report?.roundId
												? report?.roundId
														?.split("#")
														?.filter((part) => /\d/.test(part))
														?.join(" ")
												: "",
										5
									)}
								</Id>
							</CopyId>
							<BonusInfo>
								{translate("BONUS")}:{" "}
								{report?.walletContributions?.length > 0 &&
								report?.walletContributions[0]?.walletType == "Real"
									? "NO"
									: "YES"}
							</BonusInfo>
							<PaybackInfo>
								{translate("PAYBACK")}:{" "}
								{report?.transactionType &&
								report?.transactionType?.split("#")[1] == "Rollback"
									? translate("YES")
									: translate("NO")}
							</PaybackInfo>
						</FooterInfoWrapper>
						<Note>
							{translate("NOTE")}:{" "}
							{report?.note ? report?.note : translate("THERE IS NO NOTE")}
						</Note>
					</ContentFooter>
				)
			) : null}
			<DetailsButton
				rotate={areDetailsOpen}
				onClick={() => setOpenDetailsIndex(areDetailsOpen ? null : index)}>
				<ButtonText>
					{!areDetailsOpen ? translate("DETAILS") : translate("HIDE")}
				</ButtonText>
				<ArrowDownIcon />
			</DetailsButton>
		</ContentWrapper>
	);
};

const GameContent = ({ data, translate }) => {
	const [openDetailsIndex, setOpenDetailsIndex] = useState(null);
	const isLoggedIn = useIsLoggedIn();
	const { isChatEnabled } = useChatAvailability();
	const { data: eligibility, isLoading: eligibilityLoading } = useGetChatEligibilityQuery(
		undefined,
		{
			skip: !isLoggedIn,
		}
	);
	const {
		openShare,
		closeShare,
		confirmShare,
		isSharing,
		isShareModalOpen,
		enabledChannels,
		selectedChannelId,
		setSelectedChannelId,
		canConfirmShare,
	} = useShareToChat({ translate });

	const canShareToChatEnabled = canShareToChat({
		isLoggedIn,
		isChatEnabled,
		eligibility,
		eligibilityLoading,
	});

	const ShareModal = window.TENANT === "betto" ? BettoShareChannelModal : ShareChannelModal;

	return (
		<>
			{data?.reports?.map((report, index) => (
				<GameContentRow
					key={index}
					report={report}
					index={index}
					translate={translate}
					openDetailsIndex={openDetailsIndex}
					setOpenDetailsIndex={setOpenDetailsIndex}
					canShareToChat={canShareToChatEnabled}
					onShare={openShare}
					isSharing={isSharing}
				/>
			))}
			<ShareModal
				isOpen={isShareModalOpen}
				translate={translate}
				channels={enabledChannels}
				selectedChannelId={selectedChannelId}
				onChannelChange={setSelectedChannelId}
				isSubmitting={isSharing}
				canSubmit={canConfirmShare}
				onClose={closeShare}
				onSubmit={confirmShare}
			/>
		</>
	);
};

export default GameContent;
